import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../../components/Loader";
import { CreateHandleUnsaveNavigation } from "../../../../utilities/handleUnsaveNavigation";
import { useEffect, useState } from "react";
import { Box, Grid2 } from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useNavigationGuard } from "../../../../context/NavigationGuardProvider ";
import StyledGridComp from "../../../../components/StyledGridComp";
import UncontrolledSearchSelect from "../../../../components/UncontrolledSearchSelect";
import SearchableSelect from "../../../../components/SearchableSelect";
import {
  getQuarantineDrugDataService,
  getQuarantineOnLoadData,
  processQuarantineService,
} from "../../../../services/drugOrderManagementService";
import { notifySuccess, notifyWarning } from "../../../../utilities/helper";
import QuarantineConfirm from "./QuarantineConfirm";

const QuarantineDrugOrder = () => {
  const access_token = localStorage?.getItem("access_token");

  const roleId = localStorage?.getItem("roleId");

  const [load, setLoad] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [rowData, setRowData] = useState([]);
  const [rowLength, setRowLength] = useState(0);
  const [orderNumber, setOrderNumber] = useState("");
  const [showConfirmPage, setShowConfirmPage] = useState(false);
  const [acknowledgeConfirmData, setAcknowledgeConfirmData] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loadData, setLoadData] = useState(null);

  const id = useLocation()?.state?.id;

  const handleUnsaveNavigation = CreateHandleUnsaveNavigation();
  const navigate = useNavigate();

  const createQuarantineSchema = yup.object().shape({
    applyDrugKitStatus: yup.string(),
    // .required("Apply Drug Status To All Kits is required"),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(createQuarantineSchema),
    defaultValues: {
      applyDrugKitStatus: "",
    },
  });

  useNavigationGuard(isDirty);

  const selectedStatus = watch("applyDrugKitStatus");

  useEffect(() => {
    if (id && access_token) {
      getOnLoadData(id);
    }
  }, [id]);

  useEffect(() => {
    if (loadData?.destinationSiteId && id && access_token) {
      getQuarantineDrugData(loadData?.destinationSiteId);
    }
  }, [loadData?.destinationSiteId, id]);

  // This is for applying the selected status to all the rows in the grid
  useEffect(() => {
    if (selectedStatus && rowData.length > 0) {
      const updatedRows = rowData.map((row) => {
        return { ...row, selectedStatusVal: selectedStatus };
      });
      setRowData(updatedRows);
    }
  }, [selectedStatus]);

  const columns = [
    {
      field: "kitNumber",
      headerName: "Drug Kit Number",
      width: 460,
      renderHeader: () => (
        <div className="grid-heading">{"Drug Kit Number"}</div>
      ),
      renderCell: (params) => {
        const kitNumber = params.row.kitNumber;

        return <span className="data-font">{kitNumber}</span>;
      },
    },
    {
      field: "currentStatus",
      headerName: "Current Status",
      width: 460,
      renderHeader: () => (
        <div className="grid-heading">{"Current Status"}</div>
      ),
      renderCell: (params) => {
        const currentStatus = params.row.currentStatus;

        return <span className="data-font">{currentStatus}</span>;
      },
    },
    {
      field: "selectedStatus",
      filterable: false,
      headerName: "Selected Status",
      width: 460,
      renderHeader: () => (
        <div className="grid-heading">{"Selected Status"}</div>
      ),
      renderCell: (params) => {
        const currentStatus = params.row.currentStatus;

        return (
          <UncontrolledSearchSelect
            data={statusOptions}
            optionLabel="key"
            optionKey="value"
            noDefault={true}
            isReadOnly={false}
            placeholder="Select Status"
            value={params.row.selectedStatusVal || "none"}
            onChange={(val) => {
              const updatedRows = rowData.map((row) => {
                if (row.id === params.row.id) {
                  return { ...row, selectedStatusVal: val };
                }
                return row;
              });
              setRowData(updatedRows);
            }}
          />
        );
      },
    },
  ];

  const getOnLoadData = async (orderId) => {
    try {
      setLoad(true);
      const res = await getQuarantineOnLoadData(access_token, orderId);
      if (
        res?.status == 200 &&
        res?.data?.message == "Onload Data Extracted Successfully!"
      ) {
        setLoad(false);
        setStatusOptions(
          res?.data?.result?.drugStatuses?.map((item) =>
            item?.description === ""
              ? {
                  ...item,
                  key: "None",
                  value: "00000000-0000-0000-0000-000000000000",
                }
              : {
                  key: item?.description,
                  value: item?.id,
                }
          )
        );
        setOrderNumber(res?.data?.result?.viewModel?.orderNumber);
        setLoadData(res?.data?.result?.viewModel);
      }
    } catch (err) {
      setLoad(false);
      console.log("Error", err?.message);
    }
  };

  const getQuarantineDrugData = async (siteId) => {
    setLoad(true);
    try {
      const res = await getQuarantineDrugDataService(access_token, id, siteId);
      if (res?.data?.status == 200) {
        setLoad(false);
        setRowData(
          res?.data?.result?.map((item, idx) => ({
            ...item,
            id: item.drugId || idx, // fallback to idx if no id present
          }))
        );
        setRowLength(res?.data?.result?.totalCount);
      }
    } catch (err) {
      setLoad(false);
      console.log("Err", err);
      if (err?.status === 401) {
        navigate("/unAuthorize");
      }
    }
  };

  const submitQuarantinedMaterails = async (data) => {
    try {
      setLoad(true);

      const payload = {
        orderId: id,
        orderNumber: orderNumber,
        orderAcknowledgedDate: loadData?.orderAcknowledgedDate,
        orderProcessedDate: loadData?.orderProcessedDate,
        applyDrugStatus: loadData?.applyDrugStatus,
        drugUpdateJson: JSON.stringify(
          transFormRowDataForPayload(rowData, statusOptions)
        ),
        destinationSiteId: loadData?.destinationSiteId,
        desinationDepotId: loadData?.desinationDepotId
          ? loadData?.desinationDepotId
          : "00000000-0000-0000-0000-000000000000",
      };
      const res = await processQuarantineService(access_token, payload, roleId);

      if (
        res?.data?.message == "Approved Successfully!" ||
        res?.data?.status === 200
      ) {
        setLoad(false);
        notifySuccess("Order Processed Successfully!");
        // navigate("/drug-shipment-management");
        setShowConfirmPage(true);
        setAcknowledgeConfirmData(res?.data?.result);
      } else if (
        res?.data?.message === "One or more drugs require a status update.!"
      ) {
        notifyWarning(res?.data?.message);
        setLoad(false);
      }
    } catch (err) {
      console.log("Err", err);
      setShowConfirmPage(false);
      setAcknowledgeConfirmData(null);
      if (err?.status === 401) {
        navigate("/unAuthorize");
      }
      setLoad(false);
    }
  };

  // This function will make changes to the grid data such that it will create the statuses array with the selected status from the grid Select along with the object such that the key will be the drugId
  function transFormRowDataForPayload(dataArray, statusOptions) {
    return dataArray.reduce((acc, item) => {
      const { selectedStatusVal, ...rest } = item;

      // Find matching status name
      const matchedStatus = statusOptions.find(
        (status) => status.value === selectedStatusVal
      );

      acc[item.drugId] = {
        drugId: item.drugId,
        siteName: item.siteName || "", // fallback if not in original
        kitNumber: item.kitNumber,
        currentStatus: item.currentStatus,
        currentStatusId: item.currentStatusId,
        selectedStatus: selectedStatusVal,
        statuses: [
          {
            id: selectedStatusVal,
            name: matchedStatus ? matchedStatus.key : "",
          },
        ],
      };

      return acc;
    }, {});
  }

  return (
    <>
      {showConfirmPage ? (
        <QuarantineConfirm
          data={acknowledgeConfirmData}
          setShowConfirm={setShowConfirmPage}
        />
      ) : (
        <>
          {load ? (
            <Loader />
          ) : (
            <div className="content-body">
              <p className="bread-crumbs-link-container">
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) =>
                    handleUnsaveNavigation(e, isDirty, "", navigate)
                  }
                >
                  Home
                </button>{" "}
                |{" "}
                <button
                  className="bread-crumbs-btn"
                  onClick={(e) =>
                    handleUnsaveNavigation(
                      e,
                      isDirty,
                      "drug-shipment-management",
                      navigate
                    )
                  }
                >
                  Drug Order Management
                </button>{" "}
                |{" "}
                <button className="bread-crumbs-btn">
                  Process Quarantine{" "}
                </button>
                - <button className="bread-crumbs-btn">#{orderNumber} </button>-{" "}
                <b style={{ color: "gray" }}>
                  {localStorage?.getItem("studyName")}
                </b>
              </p>

              <Grid2
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%", margin: "0 auto" }}
              >
                <Grid2 item md={4}>
                  <p className="page-heading">Process Quarantined Materials</p>
                </Grid2>
              </Grid2>

              <div>
                <Box
                  component="form"
                  autoComplete="off"
                  sx={{
                    height: "auto",
                  }}
                  onSubmit={handleSubmit(submitQuarantinedMaterails)}
                >
                  <Grid2
                    container
                    alignItems="center"
                    justifyContent={"space-between"}
                    sx={{ width: "50%", margin: "0 auto" }}
                    gap={1}
                  >
                    <Grid2 size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}>
                      <div>
                        <p className="labelStyles">
                          Apply Drug Status to All Kits
                          {/* <span>*</span> */}
                        </p>
                        <SearchableSelect
                          control={control}
                          data={statusOptions || []}
                          noDefault={true}
                          name={"applyDrugKitStatus"}
                          optionKey="value"
                          optionLabel="key"
                        />
                      </div>
                      {errors.applyDrugKitStatus && (
                        <div className="error-container">
                          {errors.applyDrugKitStatus.message}
                        </div>
                      )}
                    </Grid2>
                  </Grid2>

                  <div
                    style={{
                      margin: "40px 0px",
                      maxHeight: "500px",
                      overflowY: "auto",
                    }}
                  >
                    <StyledGridComp
                      rows={rowData}
                      columns={columns}
                      page={page}
                      setPage={setPage}
                      pageSize={pageSize}
                      setPageSize={setPageSize}
                      length={rowLength}
                      isPaginationClientSide={true}
                    ></StyledGridComp>
                  </div>

                  <Grid2
                    alignItems={"end"}
                    justifyContent="end"
                    container
                    gap={3}
                    sx={{ marginTop: "2%" }}
                  >
                    <Grid2 item md={6}>
                      <button
                        type="button"
                        className="create-btn-outlined"
                        onClick={(e) => {
                          e.preventDefault();
                          handleUnsaveNavigation(
                            e,
                            isDirty,
                            "drug-shipment-management",
                            navigate
                          );
                        }}
                      >
                        Cancel
                      </button>
                    </Grid2>
                    <Grid2 item md={6}>
                      <button type="submit" className="create-btn">
                        Save
                      </button>
                    </Grid2>
                  </Grid2>
                </Box>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default QuarantineDrugOrder;
